import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Company } from '../../types/career';
import { config } from '../../utils/config';

interface BuildingDetailProps {
  company: Company;
  onClose: () => void;
}

const BuildingDetail = ({ company, onClose }: BuildingDetailProps) => {
  const [detailContent, setDetailContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const seekingRef = useRef(false);

  const formatDate = (dateStr: string) => {
    const [year, month] = dateStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Lock body scroll when modal is open and compensate for scrollbar
  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.paddingRight = originalBodyPaddingRight;
    };
  }, []);

  // Load detailed content dynamically
  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);

      try {
        // Try to load company-specific detail HTML file
        const response = await fetch(`${config.staticUrl}/career/${company.id}.html`);
        if (response.ok) {
          const html = await response.text();
          setDetailContent(html);
        } else {
          // No detailed content available, use basic info
          setDetailContent(null);
        }
      } catch {
        // Silently fail - just show basic info
        setDetailContent(null);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [company.id]);

  // Check if audio narration is available (supports mp3 and wav)
  useEffect(() => {
    const checkAudioFormats = async () => {
      const formats = ['mp3', 'wav'];

      for (const format of formats) {
        const audioUrl = `${config.staticUrl}/career/${company.id}.${format}`;
        try {
          const response = await fetch(audioUrl, { method: 'HEAD' });
          if (response.ok) {
            setAudioAvailable(true);
            setAudioError(null);
            const audio = new Audio(audioUrl);
            audio.preload = 'metadata';
            audio.addEventListener('ended', () => {
              setIsPlaying(false);
              setCurrentTime(0);
            });
            audio.addEventListener('error', () => {
              setAudioError('Failed to load audio. Please try again later.');
              setIsPlaying(false);
            });
            audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
            audio.addEventListener('timeupdate', () => {
              if (!seekingRef.current) setCurrentTime(audio.currentTime);
            });
            audioRef.current = audio;
            return;
          }
        } catch {
          // Continue to next format
        }
      }
      setAudioAvailable(false);
    };

    checkAudioFormats();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [company.id]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    setAudioError(null);

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setAudioError('Failed to play audio. The file may be unavailable.');
        setIsPlaying(false);
      });
    }
  };

  return createPortal(
    <div
      className="fixed z-50"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="absolute bg-black bg-opacity-50"
        style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      />

      {/* Modal */}
      <div
        className="relative rounded-xl shadow-2xl max-h-[80vh] flex flex-col animate-fadeIn"
        style={{
          width: '100%',
          maxWidth: '42rem',
          backgroundColor: 'var(--bg-secondary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 border-b p-4"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
        >
          {/* Audio narration button */}
          {audioAvailable && (
            <>
              <button
                onClick={toggleAudio}
                className="w-full mb-2 flex items-center justify-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: isPlaying ? '#dc2626' : '#2563eb',
                  color: 'white',
                }}
              >
                {isPlaying ? (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                    <span>Pause Narration</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span>Play Audio Narration</span>
                  </>
                )}
              </button>
              {audioError && (
                <p className="text-red-500 text-sm text-center mb-2">{audioError}</p>
              )}
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-mono w-10 text-right" style={{ color: 'var(--text-secondary)' }}>
                  {formatTime(currentTime)}
                </span>
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  step={0.1}
                  className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer bg-gray-300 dark:bg-gray-600"
                  style={{ accentColor: '#3b82f6' }}
                  onMouseDown={() => { seekingRef.current = true; }}
                  onTouchStart={() => { seekingRef.current = true; }}
                  onInput={(e) => setCurrentTime(Number((e.target as HTMLInputElement).value))}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (audioRef.current) audioRef.current.currentTime = val;
                    setCurrentTime(val);
                    seekingRef.current = false;
                  }}
                />
                <span className="text-xs font-mono w-10" style={{ color: 'var(--text-secondary)' }}>
                  {formatTime(duration)}
                </span>
                <button
                  onClick={() => {
                    const newRate = playbackRate === 1 ? 2 : 1;
                    setPlaybackRate(newRate);
                    if (audioRef.current) audioRef.current.playbackRate = newRate;
                  }}
                  className="px-2.5 py-0.5 text-xs font-bold rounded-full transition-all duration-150"
                  style={{
                    backgroundColor: playbackRate === 2 ? '#2563eb' : 'var(--border-color)',
                    color: playbackRate === 2 ? 'white' : 'var(--text-secondary)',
                  }}
                >
                  2x
                </button>
              </div>
            </>
          )}

          <div className="flex items-start justify-between">
            <div className="flex-1">
              {company.logo && (
                <img
                  src={company.logo}
                  alt={company.name}
                  className="w-16 h-16 object-contain mb-2"
                />
              )}
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{company.name}</h2>
              <p className="text-lg font-semibold text-blue-600">{company.position}</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {formatDate(company.startDate)} - {company.endDate ? formatDate(company.endDate) : 'Present'}
                <span className="mx-2">|</span>
                {company.location}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-2xl font-bold p-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-blue-600" style={{ borderColor: 'var(--border-color)', borderTopColor: '#2563eb' }}></div>
              <span className="ml-3" style={{ color: 'var(--text-secondary)' }}>Loading details...</span>
            </div>
          ) : (
            <>
              {/* Description */}
              <div className="mb-6">
                <p className="leading-relaxed" style={{ color: 'var(--text-primary)' }}>{company.description}</p>
              </div>

              {/* Dynamic content from HTML file */}
              {detailContent && (
                <div
                  className="mb-6 prose prose-sm max-w-none"
                  style={{ color: 'var(--text-primary)' }}
                  dangerouslySetInnerHTML={{ __html: detailContent }}
                />
              )}

              {/* Highlights */}
              {company.highlights && company.highlights.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Key Highlights</h3>
                  <ul className="space-y-2">
                    {company.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-green-500 mr-2 mt-1">&#10003;</span>
                        <span style={{ color: 'var(--text-primary)' }}>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Technologies */}
              {company.technologies && company.technologies.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Technologies Used</h3>
                  <div className="flex flex-wrap gap-2">
                    {company.technologies.map((tech, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-blue-600 bg-opacity-20 text-blue-600 text-sm rounded-full font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="sticky bottom-0 border-t px-6 py-3 text-center"
          style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}
        >
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Press ESC or click outside to close</p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BuildingDetail;
